// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import {
  setActividadId,
  setBookingDetailComponents,
  setComments,
  setComponents,
  setPax,
  setPickUpDropOffs,
  setProductosBooking,
  setProductosNotaCredito,
} from './actions'
import { BOOKING_DETAIL_GENERAR_NOTA_CREDITO, BOOKING_DETAIL_SET_SELECT } from './actionsTypes'
import { coleccionExpandida } from './utils/coleccionExpandida'

import { StateCatalogos } from '../../catalogos/CatalogosReducer'
import { RootState } from '../../store'

import { setCurrentBooking, setModalNotaCredito } from '../booking/actions'

import { get, patch as patchBD, post } from '@/common/helpers/api_helper'
import { notifications } from '@/common/helpers/notifications'
import { dateTimeToDateForms, toDate } from '@/common/helpers/utilsDate'
import { IBooking, IBookingsState, ICambioEstadoBooking } from '@/common/interfaces/booking'
import { StateBookingDetail, IBookingDetail } from '@/common/interfaces/detailBooking'
import { Estado } from '@/common/interfaces/estado'

import { getRuntimeConfig } from '@/core/config/runtimeConfig'
import { msalInstance } from '@/core/msal/msal'
import { ESTADO_ENVIADO_NOTA_CREDITO } from '@/modules/Booking/constants/estadosBooking'

const BookingDetail = (state: RootState): StateBookingDetail => state.BookingDetail
const Booking = (state: RootState): IBookingsState => state.Booking
const Catalogos = (state: RootState): StateCatalogos => state.Catalogos

const getUserByEmail = (email: string): Promise<void> => {
  return get(`Usuarios?$filter=NombreUsuario eq '${email}'`)
    .then((res) => {
      if (res.value.length === 0) {
        return { UsuarioId: 0 }
      }

      return res.value[0]
    })
    .catch((error) => {
      notifications({ ex: error })
    })
}

const TIPO_COMENTARIO_FACTURACION = 'Facturación'

/**
 * Obtiene los comentarios visibles del BookingDetail combinando dos fuentes:
 *   1) BookingDetail.Comentario → TODOS (histórico / sincronizados desde Salesforce)
 *   2) BookingDetail.Actividad.Comentario → SOLO los de tipo "Facturación"
 *      (los nuevos comentarios de facturación viven en la Actividad, no en BookingDetail)
 *
 * Devuelve también el ActividadId asociado al BookingDetail (necesario para crear
 * nuevos comentarios de facturación contra el endpoint ComentarioBusiness/CrearComentarioFacturacion).
 */
const getComenByActividadId = (bookingDetailId: number): Promise<{ actividadId: number; comentarios: any[] }> => {
  return get(
    `/BookingDetails?$select=BookingDetailId` +
      `&$expand=` +
      `Comentario($expand=TipoComentario($select=Nombre)),` +
      `Actividad($select=ActividadId;` +
      `$expand=Comentario(` +
      `$filter=TipoComentario/Nombre eq '${TIPO_COMENTARIO_FACTURACION}';` +
      `$expand=TipoComentario($select=Nombre)` +
      `)` +
      `)` +
      `&$filter=BookingDetailId eq ${bookingDetailId}`,
  )
    .then((result) => {
      const bookingDetail = result.value.find((bd: any) => bd.BookingDetailId === bookingDetailId)

      if (!bookingDetail) {
        return { actividadId: 0, comentarios: [] }
      }

      // BookingDetail tiene relación 1:1 con Actividad; el guard de Array.isArray
      // cubre variaciones del serializador OData
      const actividad = Array.isArray(bookingDetail.Actividad) ? bookingDetail.Actividad[0] : bookingDetail.Actividad

      const comentariosBookingDetail = Array.isArray(bookingDetail.Comentario) ? bookingDetail.Comentario : []
      // El gestor copia cada comentario del BookingDetail a TODAS sus Actividades, asi que la lista de la
      // actividad trae ecos de los de Salesforce. Solo interesan los locales (CommentIdSf == null), que son
      // los de facturacion creados en Perseverance; lo demas ya viene en comentariosBookingDetail.
      const comentariosActividadFacturacion = (
        Array.isArray(actividad?.Comentario) ? actividad.Comentario : []
      ).filter((c: any) => (c.CommentIdSf ?? null) === null)

      return {
        actividadId: actividad?.ActividadId ?? 0,
        comentarios: [...comentariosBookingDetail, ...comentariosActividadFacturacion],
      }
    })
    .catch((error) => {
      notifications({ ex: error })

      return { actividadId: 0, comentarios: [] }
    })
}

const getPaxByID = (id: number): Promise<any[]> => {
  return get(
    `/BookingDetails?$select=BookingDetailId&$expand=AsignacionPasajero($expand=Pasajero($expand=TipoPasajero($select=Valor)))&$filter=BookingDetailId eq ${id}`,
  )
    .then((paxs) => {
      return coleccionExpandida(paxs, 'AsignacionPasajero').sort(
        (
          a: { Pasajero: { NumeroPasajero: number } },
          b: {
            Pasajero: { NumeroPasajero: number }
          },
        ) => {
          return a.Pasajero.NumeroPasajero - b.Pasajero.NumeroPasajero
        },
      )
    })
    .catch((error) => {
      notifications({ ex: error })

      // Sin esto la saga guarda `undefined` y el panel entero del booking se cae en `.length`
      return []
    })
}

const getPickUpDropByID = (id: number): Promise<any[]> => {
  return get(
    // El nombre de la propiedad de navegacion es `PickUpDropOff` (asi lo declara xts_metadata.xml
    // y asi lo escriben los otros 57 usos); aca estaba escrito distinto.
    `/BookingDetails?$select=BookingDetailId&$expand=PickUpDropOff($expand=Vuelo($select=Numero))&$filter=BookingDetailId eq ${id}`,
  )
    .then((pickupDrop) => {
      return coleccionExpandida(pickupDrop, 'PickUpDropOff')
    })
    .catch((error) => {
      notifications({ ex: error })

      return []
    })
}

const getComponent = (pleId: number): Promise<void> => {
  return get(
    `EstructuraProductosCompuesto?$expand=Componente($expand=Estacion,ComponentType)&$filter=ProductoEntidadLegalId eq ${pleId}`,
  )
    .then((legoland) => {
      if (legoland.value.length === 0) {
        return []
      }

      return legoland.value
    })
    .catch((error) => {
      notifications({ ex: error })

      // Igual que getPaxByID y getPickUpDropByID: sin esto la saga guarda `undefined`,
      // setComponents hace `.sort` sobre él y el TypeError mata bookingDetailSaga entero —
      // desde ahí clickear productos no carga nada y el Guardar de la nota de crédito no
      // responde hasta recargar.
      return []
    })
}

const getComponentByBookingDetailId = (bookingDetailId: number): Promise<void> => {
  return get(
    `BookingDetails?$select=ComponenteBookingDetail,BookingDetailId
    &$expand=ComponenteBookingDetail($select=Eliminado,Componente,ComponenteBookingDetailId;
    $expand=Componente($select=Nombre,Descripcion;$expand=ComponentType($select=Nombre));
    $filter=Eliminado eq false)&$filter=BookingDetailId eq ${bookingDetailId}`,
  )
    .then((data) => {
      if (data.value.length === 0) {
        return []
      }

      return data.value
    })
    .catch((error) => {
      notifications({ ex: error })

      // Ver getComponent: `undefined` aquí es `response.length` sobre undefined en la saga.
      return []
    })
}

function* getPickupByID(id: number): Generator {
  const pickUpDrop = yield call(getPickUpDropByID, id)

  yield put(setPickUpDropOffs(pickUpDrop))
}

// eslint-disable-next-line camelcase
function* getComentariosByBD_Id(id: number): Generator {
  const result = yield call(getComenByActividadId, id)
  const { actividadId, comentarios } = result as { actividadId: number; comentarios: any[] }

  yield put(setActividadId(actividadId))
  yield put(setComments(comentarios))
}

// eslint-disable-next-line camelcase
function* getPasajerosAsignadoByBD_Id(id: number): Generator {
  const paxs = yield call(getPaxByID, id)

  yield put(setPax(paxs))
}

function* getComponentsLegolandByPLEId(pleId: number): any {
  const components = yield call(getComponent, pleId)

  yield put(setComponents(components))
}

function* getComponentsByBookingDetailId(bookingDetailId: number): Generator {
  const response = yield call(getComponentByBookingDetailId, bookingDetailId)

  const components = response.length > 0 ? response[0].ComponenteBookingDetail : []

  yield put(setBookingDetailComponents(components))
}

// eslint-disable-next-line camelcase
function* getPickUpDropOffByBD_Id(id: number): any {
  yield call(getPickupByID, id)
}

// PATCH y no PUT: se mandan SOLO los campos de la nota de crédito. Con el PUT viajaba la entidad
// entera tal como la dejó el $expand del GET, y eso traía dos problemas: reescribía todas las
// columnas escalares de la fila (pisando lo que otro hubiera cambiado) y arrastraba el grafo de
// navegaciones a medio poblar, que el backend rechazaba con 400 por «campos requeridos» —era lo
// que bloqueaba emitir la nota—. Ver PatchBookingDetail en BookingDetailsController.
function* actualizarBDProductosNotaCredito(productosNotaCredito: IBookingDetail[], email: string): any {
  const usuarioId = yield call(getUserByEmail, String(email))

  for (const i of productosNotaCredito) {
    yield call(patchBD, `BookingDetails(${i.BookingDetailId})`, {
      RequiereNotaCredito: true,
      FechaNotaCredito: dateTimeToDateForms(new Date()),
      UsuarioIdCreacionNotaCredito: usuarioId.UsuarioId,
      ValorNotaCredito: i.ValorNotaCredito,
      ObservacionNotaCredito: i.ObservacionNotaCredito,
    })
  }
}

// Desmarca en la BD los productos que estaban en la nota y el usuario devolvió al booking.
// Va ANTES de enviar: el ERP arma la nota con TODOS los RequiereNotaCredito = true, así que un
// retirado sin desmarcar entraría igual a la nota (los marcados de intentos fallidos quedaban
// en ese limbo — en UAT hay 10). Al final refresca el store para que la ventana quede coherente.
function* desmarcarBDProductosRetirados(productosBooking: IBookingDetail[]): any {
  const retirados = productosBooking.filter((p) => p.RequiereNotaCredito)

  // Mismo criterio que actualizarBDProductosNotaCredito: PATCH con solo los campos de la nota.
  for (const i of retirados) {
    yield call(patchBD, `BookingDetails(${i.BookingDetailId})`, {
      RequiereNotaCredito: false,
      ValorNotaCredito: null,
      ObservacionNotaCredito: null,
      FechaNotaCredito: null,
      UsuarioIdCreacionNotaCredito: null,
    })
  }

  if (retirados.length > 0) {
    yield put(
      setProductosBooking(
        productosBooking.map((p) =>
          p.RequiereNotaCredito
            ? { ...p, RequiereNotaCredito: false, ValorNotaCredito: null, ObservacionNotaCredito: null }
            : p,
        ),
      ),
    )
  }
}

// Devuelve si la nota llegó a JDE: el que llama solo limpia el borrador cuando hubo éxito.
function* enviarJde(booking: IBooking, estadoCerrado: Estado): Generator {
  const estadoAntiguo = booking.EstadoId
  const bookingNotaCredito = { ...booking }

  bookingNotaCredito.EstadoId = estadoCerrado.EstadoId
  bookingNotaCredito.Estado = estadoCerrado

  const newCambioEstadoNotaCredito: ICambioEstadoBooking = {
    BookingId: bookingNotaCredito.BookingId,
    EstadoAntiguoId: estadoAntiguo,
    EstadoNuevoId: estadoCerrado.EstadoId,
    FechaCambio: new Date(),
  }

  try {
    const response = yield call(
      post,
      `GenerarOrdenesFacturacion(${bookingNotaCredito.BookingId},NotaCredito,${getRuntimeConfig().REGION})`,
      '',
    )

    if (response) {
      bookingNotaCredito.CodigoJdeNotaCredito = response.codigoIntegracion
      bookingNotaCredito.SyncStatusJdeNotaCredito = response.syncStatusIntegracion
      bookingNotaCredito.UltimaFechaIntegracionJdeNotaCredito = response.fechaIntegracion
        ? toDate(response.fechaIntegracion)
        : undefined
    }

    yield call(post, `CambiosEstadoBooking`, newCambioEstadoNotaCredito)
    yield put(setCurrentBooking(bookingNotaCredito))
    yield put(setModalNotaCredito(false))
    notifications({
      title: 'Éxito',
      message: `Se ha generado la nota de crédito para el booking con código: ${bookingNotaCredito.BookingCode}`,
      toastType: 'success',
      seRegistra: true,
    })

    return true
  } catch (error) {
    notifications({ ex: error })
    yield put(setModalNotaCredito(false))

    return false
  }
}

function* getComplementos(): Generator {
  const { detailSelected }: StateBookingDetail = yield select(BookingDetail)
  const { BookingDetailId, ProductoEntidadLegalId } = detailSelected

  yield fork(getComentariosByBD_Id, BookingDetailId)
  yield fork(getPasajerosAsignadoByBD_Id, BookingDetailId)
  yield fork(getPickUpDropOffByBD_Id, BookingDetailId)
  yield fork(getComponentsLegolandByPLEId, ProductoEntidadLegalId)
  yield fork(getComponentsByBookingDetailId, BookingDetailId)
}

// Se exporta para poder probar el orden de los efectos sin levantar el store completo
export function* generarNotaCredito(): Generator {
  const { productosNotaCredito, productosBooking }: StateBookingDetail = yield select(BookingDetail)
  const { selectedBooking }: IBookingsState = yield select(Booking)
  const { estados }: StateCatalogos = yield select(Catalogos)
  const estadoNotaCredito = estados['Booking'][ESTADO_ENVIADO_NOTA_CREDITO]
  const account = msalInstance.getActiveAccount()
  const email = account!.username

  // La ventana NO se cierra: un booking puede llevar varias notas hasta agotar el disponible de
  // cada producto, y cerrarla obligaba a reabrirla para armar la siguiente. Se contradecia con el
  // propio final de esta saga, que devuelve los productos limpios a la izquierda «para que la
  // ventana quede lista para armar otra sin recargar» — y ya la habia cerrado.
  yield put(setModalNotaCredito(true))

  try {
    // Primero los retirados: si quedaran marcados, el ERP los metería igual a la nota.
    yield call(desmarcarBDProductosRetirados, productosBooking)

    // Con la nota vacía, Guardar es SOLO la limpieza de los retirados: no se envía nada a JDE.
    if (productosNotaCredito.length === 0) {
      notifications({
        title: 'Éxito',
        message: 'Se quitó la marca de nota de crédito de los productos retirados; no se envió ninguna nota.',
        toastType: 'success',
        seRegistra: true,
      })

      return
    }

    // El ERP arma la nota solo con los BookingDetails que ya tienen RequiereNotaCredito = true, y
    // marcarlos es un PUT por producto: hay que esperar a que terminen TODOS antes de invocarlo.
    // Con `fork` + delay(4000) el tiempo era una suposición, no una dependencia: con varios
    // productos o red lenta la nota salía incompleta y el faltante ya no se puede corregir.
    yield call(actualizarBDProductosNotaCredito, productosNotaCredito, email)
    const exito = yield call(enviarJde, selectedBooking, estadoNotaCredito)

    // El backend creó la nota y LIMPIÓ los flags (#40996: los flags son el borrador de la
    // próxima nota): se refleja en memoria para que la ventana quede lista para armar otra
    // sin recargar — los productos vuelven a la izquierda, limpios.
    if (exito) {
      const limpios = productosNotaCredito.map((p) => ({
        ...p,
        RequiereNotaCredito: false,
        ValorNotaCredito: null,
        ObservacionNotaCredito: null,
        selected: false,
        inEdit: false,
      }))
      const { productosBooking: bookingActual }: StateBookingDetail = yield select(BookingDetail)

      yield put(setProductosBooking([...bookingActual, ...limpios]))
      yield put(setProductosNotaCredito([]))
    }
  } catch (error) {
    // Si falla algún PUT no se llama al ERP: mejor no emitir la nota que emitirla incompleta.
    // El catch también evita que el error suba al rootSaga (que usa `fork`) y tumbe el árbol.
    notifications({ ex: error })
  } finally {
    // El modal es un loading a pantalla completa; sin esto quedaba girando para siempre
    yield put(setModalNotaCredito(false))
  }
}

function* watchSelectBookingDetail(): Generator {
  // takeLatest: al clickear rápido entre dos productos, las cinco consultas del producto
  // anterior seguían vivas y podían responder después — los pasajeros/comentarios/pickup de un
  // producto quedaban pintados bajo la selección del otro. La cancelación alcanza a los cinco
  // forks adjuntos de getComplementos.
  yield takeLatest(BOOKING_DETAIL_SET_SELECT, getComplementos)
}

function* watchGenerarNotaCredito(): Generator {
  yield takeEvery(BOOKING_DETAIL_GENERAR_NOTA_CREDITO, generarNotaCredito)
}

function* bookingDetailSaga(): Generator {
  yield all([fork(watchSelectBookingDetail), fork(watchGenerarNotaCredito)])
}

export default bookingDetailSaga
